import { useEffect, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import {
  isDateRangeWithinNormalPerformancePricingRefreshWindow,
} from "connected-components/dashboard/helpers/date-calculations"
import { getInstrumentSuffixForIntradayContributionPrefix } from "gatherers/contributors-data-integrator.utils"

/**
 * Invalidates TanStack Query cache entries for active gatherers when the intraday `asOf`
 * timestamp advances.
 *
 * Eligibility rules:
 *  - Skip if `intradayBatchMetadata.id` is set — a batch is active; batch-mode callers own their refresh.
 *  - `intradayContribution*` gatherers: always invalidated (fixed short periods, ungated).
 *  - All other gatherers: only invalidated when the date range is ≤ 1 month.
 */
export function useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate) {
  const queryClient = useQueryClient()
  const prevAsOfRef = useRef(null)

  useEffect(() => {
    const asOf = intradayBatchMetadata?.asOf
    if (!asOf || asOf === prevAsOfRef.current || intradayBatchMetadata?.id) return

    prevAsOfRef.current = asOf

    const withinRefreshWindow = isDateRangeWithinNormalPerformancePricingRefreshWindow(startDate, endDate)

    for (const gatherer of gatherers) {
      if (!gatherer?.key) continue

      const isContribution = getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix) != null
      if (!isContribution && !withinRefreshWindow) continue

      queryClient.invalidateQueries({
        queryKey: gatherer.graphqlKey ?? gatherer.key,
        exact: true,
        refetchType: "active",
      })
    }
  }, [intradayBatchMetadata, gatherers, queryClient, startDate, endDate])
}
